import React, { useEffect, useState, useCallback } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight, Users, Heart, Globe, Package, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { heroSlides, categories, stats, aboutInfo } from '../mock';

const Home = () => {
  // Hero Slider
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, duration: 30 });
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setCurrentSlide(emblaApi.selectedScrollSnap());
    };

    emblaApi.on('select', onSelect);
    onSelect();

    const autoplay = setInterval(() => {
      emblaApi.scrollNext();
    }, 5000);

    return () => {
      clearInterval(autoplay);
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  // Counter Animation
  const [counts, setCounts] = useState({ users: 0, satisfaction: 0, countries: 0, products: 0 });
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
          animateCounters();
        }
      },
      { threshold: 0.5 }
    );

    const statsElement = document.getElementById('stats-section');
    if (statsElement) observer.observe(statsElement);

    return () => observer.disconnect();
  }, [isVisible]);

  const animateCounters = () => {
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    let step = 0;
    const counter = setInterval(() => {
      step++;
      const progress = step / steps;

      setCounts({
        users: Math.floor(15 * progress),
        satisfaction: Math.floor(98 * progress),
        countries: Math.floor(100 * progress),
        products: Math.floor(23 * progress)
      });

      if (step >= steps) clearInterval(counter);
    }, interval);
  };

  const getIconComponent = (iconName) => {
    const icons = { Users, Heart, Globe, Package };
    return icons[iconName];
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Slider */}
      <section className="relative h-[600px] md:h-[700px] overflow-hidden mt-20">
        <div ref={emblaRef} className="overflow-hidden h-full">
          <div className="flex h-full">
            {heroSlides.map((slide) => (
              <div key={slide.id} className="flex-[0_0_100%] min-w-0 relative">
                <div 
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url(${slide.image})` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-[#001F3F]/80 to-[#001F3F]/40" />
                </div>
                <div className="relative h-full flex items-center">
                  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                    <div className="max-w-2xl -mt-16">
                      <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 animate-fade-in">
                        {slide.title}
                      </h1>
                      <p className="text-xl md:text-2xl text-white/90 mb-12">
                        {slide.subtitle}
                      </p>
                      <Button 
                        onClick={() => window.open('https://om.run/!adxpokr', '_blank')}
                        className="bg-white text-[#001F3F] hover:bg-gray-100 px-8 py-6 text-lg font-semibold rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
                      >
                        Alışverişe Başla
                        <ArrowRight className="ml-2" size={20} />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Slider Controls */}
        <button
          onClick={scrollPrev}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white p-3 rounded-full transition-all duration-200"
        >
          <ChevronLeft size={24} />
        </button>
        <button
          onClick={scrollNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white p-3 rounded-full transition-all duration-200"
        >
          <ChevronRight size={24} />
        </button>

        {/* Slider Dots */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex space-x-2">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => emblaApi?.scrollTo(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                currentSlide === index ? 'bg-white w-8' : 'bg-white/50'
              }`}
            />
          ))}
        </div>
      </section>

      {/* Welcome Section */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-[#001F3F] mb-6">
              One More International'a Hoş Geldiniz
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              {aboutInfo.mainText}
            </p>
          </div>
        </div>
      </section>

      {/* Categories */}
      {categories.map((category) => (
        <CategorySection key={category.id} category={category} />
      ))}

      {/* Stats Section */}
      <section id="stats-section" className="py-20 bg-gradient-to-br from-[#001F3F] to-[#003366]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const IconComponent = getIconComponent(stat.icon);
              const values = [counts.users, counts.satisfaction, counts.countries, counts.products];
              const displayValue = index === 0 ? `${values[0]}M+` : index === 1 ? `%${values[1]}` : index === 2 ? `${values[2]}+` : values[3];
              
              return (
                <div 
                  key={stat.id}
                  className="text-center p-8 bg-white/10 backdrop-blur-sm rounded-2xl hover:bg-white/20 transition-all duration-300 hover:scale-105"
                >
                  <div className="flex justify-center mb-4">
                    <div className="p-4 bg-white/20 rounded-full">
                      <IconComponent size={40} className="text-white" />
                    </div>
                  </div>
                  <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                    {displayValue}
                  </div>
                  <div className="text-white/80 text-lg">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#001F3F] mb-6">
            Hemen Kayıt Olun ve Alışverişe Başlayın
          </h2>
          <p className="text-lg text-gray-700 mb-8">
            Kaliteli ve eşsiz ürünlerimizi keşfetmek için hemen kayıt olun.
          </p>
          <Button 
            onClick={() => window.open('https://om.run/!adxpokr', '_blank')}
            className="bg-[#001F3F] hover:bg-[#003366] text-white px-12 py-6 text-lg font-semibold rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
          >
            Kayıt Ol ve Alışverişe Başla
            <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </section>
    </div>
  );
};

// Category Section Component
const CategorySection = ({ category }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    loop: false, 
    slidesToScroll: 1,
    align: 'start'
  });

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  return (
    <section className="py-16 bg-white border-t border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Category Header Image */}
        <div className="mb-8">
          <img 
            src={category.image} 
            alt={category.name}
            className="w-full h-auto rounded-2xl shadow-xl"
          />
        </div>

        {/* Category Title */}
        <h3 className="text-3xl md:text-4xl font-bold text-[#001F3F] text-center mb-4">{category.name}</h3>

        {/* Category Description */}
        <p className="text-lg text-gray-700 text-center mb-12 max-w-3xl mx-auto">
          {category.description}
        </p>

        {/* Products Slider */}
        <div className="relative">
          <div ref={emblaRef} className="overflow-hidden">
            <div className="flex gap-6">
              {category.products.map((product) => (
                <div 
                  key={product.id} 
                  className="flex-[0_0_100%] min-w-0 sm:flex-[0_0_50%] lg:flex-[0_0_33.333%]"
                >
                  <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105">
                    <div className="aspect-[3/2] overflow-hidden">
                      <img 
                        src={product.image} 
                        alt={product.name}
                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <h4 className="text-lg font-semibold text-[#001F3F] mb-2">{product.name}</h4>
                      <p className="text-gray-600 text-sm">{product.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Slider Controls */}
          <button
            onClick={scrollPrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-[#001F3F] hover:bg-[#003366] text-white p-3 rounded-full shadow-xl transition-all duration-200 z-10"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={scrollNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-[#001F3F] hover:bg-[#003366] text-white p-3 rounded-full shadow-xl transition-all duration-200 z-10"
          >
            <ChevronRight size={24} />
          </button>
        </div>

        {/* CTA Button */}
        <div className="mt-12 text-center">
          <Button 
            onClick={() => window.open('https://om.run/!adxpokr', '_blank')}
            className="bg-[#001F3F] hover:bg-[#003366] text-white px-10 py-4 text-base font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
          >
            Alışverişe Başla
            <ArrowRight className="ml-2" size={18} />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Home;